import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import { TrackEditor } from '../tracks/TrackEditor';
import type { TrackSummary } from '../tracks/TrackList';
import { albums, type AlbumDetail } from './api';

/**
 * An artist's own controls for one track, on the release it is on.
 *
 * ## Why a release needs them at all
 *
 * A profile lists the singles - what is not on one of the artist's releases -
 * because the albums are listed above it and a track does not need to be on one
 * page twice. That rule applies to the artist as well, and the moment it did,
 * the row they edited a released track from was gone: the profile list is where
 * editing, retitling and deleting have always lived.
 *
 * So the controls follow the track. Everything the profile row offers is here,
 * on the album's row, on the full page and in the drawer both - and "remove
 * from this album" joins them, because taking a track off a release and
 * changing it are the same job done in the same minute.
 *
 * ## Behind one button
 *
 * Seven controls on a row is what the album page was heading for: two order
 * arrows, a heart, a plus, a remove, an edit and a delete. The two that are
 * about this album's shape stay out in the open - order is positional and an
 * arrow is the whole gesture - and the rest go behind an ellipsis, which is
 * where a row's third-through-seventh actions belong.
 *
 * ## Whose it is
 *
 * Edit and delete are offered on the artist's own track, by username: an album
 * holds its owner's music, so there these are the same person, and on a
 * playlist - where this will be reused - they are emphatically not. The server
 * refuses either way; this is so nothing unpressable is drawn. Removing from
 * the collection follows `can_edit`, which is the collection's own permission
 * and includes a shared playlist's collaborators.
 */

/** The panel's width, and the room it wants below the trigger. */
const MENU_WIDTH = 224;
const MENU_HEIGHT = 190;
const GAP = 6;
const EDGE = 8;

/**
 * Where the panel goes, given the button it hangs off.
 *
 * Down by default, up when down will not fit, and never off the side. The same
 * measurement `AddToPlaylist` makes for the same reason: these rows sit in
 * drawers and scrolling lists, so a fixed panel has to be told where it is.
 */
function place(box: DOMRect) {
  const below = window.innerHeight - box.bottom;
  const above = box.top;
  const up = below < MENU_HEIGHT + GAP && above > below;

  const left = Math.min(
    Math.max(EDGE, box.right - MENU_WIDTH),
    window.innerWidth - MENU_WIDTH - EDGE,
  );

  return {
    position: 'fixed' as const,
    left,
    width: MENU_WIDTH,
    maxHeight: Math.min(MENU_HEIGHT, (up ? above : below) - GAP - EDGE),
    ...(up ? { bottom: window.innerHeight - box.top + GAP } : { top: box.bottom + GAP }),
  };
}

export function TrackRowMenu({
  album,
  track,
  onChanged,
}: {
  album: AlbumDetail;
  track: TrackSummary;
  /** Reload the collection this row came from. */
  onChanged: () => void;
}) {
  const me = useSession((state) => state.user);
  const client = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [armed, setArmed] = useState(false);
  const [style, setStyle] = useState<ReturnType<typeof place> | null>(null);

  const trigger = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);

  const mine = me !== null && track.owner?.username === me.username;
  const canRemove = album.can_edit === true;

  /** What moved: this collection, the artist's lists, and the profile's counts. */
  const settled = async () => {
    onChanged();
    await client.invalidateQueries({ queryKey: ['tracks'] });
    await client.invalidateQueries({ queryKey: ['profile'] });
  };

  const remove = useMutation({
    mutationFn: () => albums.removeTrack(album.id, track.id),
    onSuccess: async () => {
      setOpen(false);
      await settled();
    },
  });

  const destroy = useMutation({
    mutationFn: () => api.delete(`/tracks/${track.id}`),
    onSuccess: async () => {
      setOpen(false);
      await settled();
    },
  });

  /* Measured before paint, so it never appears in the wrong place first. */
  useLayoutEffect(() => {
    if (!open) return;

    const box = trigger.current?.getBoundingClientRect();

    if (box) setStyle(place(box));
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const follow = () => {
      const box = trigger.current?.getBoundingClientRect();

      if (box) setStyle(place(box));
    };

    const away = (event: PointerEvent) => {
      const target = event.target as Node;

      // Both refs: the panel is portalled and is no longer inside the button.
      if (trigger.current?.contains(target) || panel.current?.contains(target)) return;

      setOpen(false);
    };

    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);

    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  /* Closing disarms, so a delete nobody confirmed is not still armed later. */
  useEffect(() => {
    if (!open) setArmed(false);
  }, [open]);

  // Nothing this person may do to this row, so no button at all.
  if (!mine && !canRemove) return null;

  /*
   * Portalled into the nearest dialog, the fullscreen element, or the body -
   * the three hosts `AddToPlaylist` documents, for the same three reasons. An
   * album row appears in a drawer, on a page, and behind the now-playing
   * overlay.
   */
  const host =
    trigger.current?.closest('dialog') ??
    (typeof document === 'undefined'
      ? null
      : (document.fullscreenElement?.contains(trigger.current) ?? false)
        ? document.fullscreenElement
        : document.body);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More for ${track.title}`}
        data-track-menu={track.id}
        onClick={(event) => {
          /*
           * The drawer's rows are links. Without this, reaching for the menu
           * navigates to the track underneath it - the same guard the heart
           * and the plus beside it use.
           */
          event.preventDefault();
          event.stopPropagation();

          setOpen((was) => !was);
        }}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] transition-colors hover:bg-raised ${
          open ? 'text-ink' : 'text-soft hover:text-ink'
        }`}
      >
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
          <circle cx="3" cy="8" r="1.4" />
          <circle cx="8" cy="8" r="1.4" />
          <circle cx="13" cy="8" r="1.4" />
        </svg>
      </button>

      {open &&
        host !== null &&
        style !== null &&
        createPortal(
          <div
            ref={panel}
            role="menu"
            data-track-menu-panel={track.id}
            style={style}
            onClick={(event) => {
              // Same reason as the trigger: this can sit inside a link.
              event.preventDefault();
              event.stopPropagation();
            }}
            className="z-[70] flex flex-col overflow-hidden rounded-[var(--radius-panel)] border border-line-lit bg-raised py-1 shadow-2xl"
          >
            <p className="truncate px-3 pb-1.5 pt-1 text-[11px] text-faint">{track.title}</p>

            {mine && (
              <Item
                attr={`edit-${track.id}`}
                onClick={() => {
                  setEditing(true);
                  setOpen(false);
                }}
              >
                Edit track
              </Item>
            )}

            {canRemove && (
              <Item
                attr={`remove-${track.id}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? 'Removing…' : 'Remove from this album'}
              </Item>
            )}

            {mine && (
              /*
                Two presses, in place, exactly as the profile row does it - and
                for the same reason: a dialog over a list makes you confirm the
                deletion of something you can no longer see.
              */
              <Item
                attr={`delete-${track.id}`}
                tone="hot"
                disabled={destroy.isPending}
                onClick={() => (armed ? destroy.mutate() : setArmed(true))}
              >
                {destroy.isPending ? 'Deleting…' : armed ? 'Delete for good?' : 'Delete track'}
              </Item>
            )}

            {(remove.isError || destroy.isError) && (
              <p className="border-t border-line px-3 py-2 text-[11px] text-danger">
                That did not work. Try again.
              </p>
            )}

            {mine && (
              <p className="border-t border-line px-3 pb-1 pt-2 text-[11px] text-faint">
                Removing takes it off the album. Deleting takes it off the site.
              </p>
            )}
          </div>,
          host,
        )}

      {/*
        The editor goes to the body, not here.

        A drawer row *is* a link, and a React portal bubbles its events through
        the component tree rather than the DOM - so a dialog rendered in place
        would hand every click inside it to the anchor above, and saving a title
        would navigate to the track page. Guarding it with `preventDefault` is
        not available either: that is the form submit's own default action.
      */}
      {editing &&
        createPortal(
          <TrackEditor
            track={track}
            cover={{
              artwork: track.artwork,
              /*
               * The picker polls until the cover changes, and it wants this one
               * track - so the collection is refetched and the row picked back
               * out of it. The same note as on the profile list.
               */
              refetch: async () => {
                const fresh = await albums.get(album.id);

                return {
                  artwork: fresh.tracks.find((row) => row.id === track.id)?.artwork ?? null,
                };
              },
            }}
            onClose={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              void settled();
            }}
          />,
          document.body,
        )}
    </>
  );
}

function Item({
  attr,
  tone = 'plain',
  children,
  ...rest
}: {
  attr: string;
  tone?: 'plain' | 'hot';
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-track-menu-item={attr}
      className={`w-full px-3 py-2 text-left text-[13px] transition-colors disabled:opacity-50 ${
        tone === 'hot' ? 'text-hot-text hover:bg-hot-wash' : 'text-soft hover:bg-slate hover:text-ink'
      }`}
      {...rest}
    >
      {children}
    </button>
  );
}
